import React, { useState } from 'react';
import styled from 'styled-components';
import apiService from '../../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: var(--spacing-4);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 900px;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
`;

const ModalHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  background: linear-gradient(135deg, var(--color-secondary) 0%, #1f1e3a 100%);
  color: white;
  
  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    font-size: var(--font-size-xl);
  }
  
  .close-btn {
    position: absolute;
    top: var(--spacing-4);
    right: var(--spacing-4);
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const ModalBody = styled.div`
  padding: var(--spacing-6);
`;

const EmailForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-5);
`;

const FormSection = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-5);
  
  h4 {
    margin: 0 0 var(--spacing-4) 0;
    color: var(--color-secondary);
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-4);
  
  &:last-child {
    margin-bottom: 0;
  }
  
  label {
    display: block;
    margin-bottom: var(--spacing-2);
    font-weight: 600;
    color: var(--color-text);
  }
  
  input, textarea, select {
    width: 100%;
    padding: var(--spacing-3);
    border: 2px solid var(--color-border);
    border-radius: var(--border-radius);
    font-size: var(--font-size-base);
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: var(--color-secondary);
      box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
    }
  }
  
  textarea {
    min-height: 200px;
    resize: vertical;
    font-family: inherit;
    line-height: 1.5;
  }
  
  .help-text {
    font-size: var(--font-size-sm);
    color: var(--color-text-light);
    margin-top: var(--spacing-1);
  }
`;

const RecipientsList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-3);
  background: #f8f9fa;
  
  .recipient-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-2);
    margin-bottom: var(--spacing-1);
    background: white;
    border-radius: var(--border-radius);
    font-size: var(--font-size-sm);
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .email {
      font-weight: 500;
      color: var(--color-secondary);
    }
    
    .name {
      color: var(--color-text-light);
    }
  }
`;

const TemplateButtons = styled.div`
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-3);
`;

const TemplateButton = styled.button`
  padding: var(--spacing-2) var(--spacing-3);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  background: white;
  cursor: pointer;
  font-size: var(--font-size-sm);
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-secondary);
    background: #f8f9fa;
  }
`;

const PreviewSection = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  background: #f8f9fa;
  
  h5 {
    margin: 0 0 var(--spacing-3) 0;
    color: var(--color-secondary);
  }
  
  .preview-content {
    background: white;
    padding: var(--spacing-4);
    border-radius: var(--border-radius);
    border: 1px solid var(--color-border);
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
  }
`;

const ModalFooter = styled.div`
  padding: var(--spacing-6);
  border-top: 1px solid var(--color-border);
  background: #f8f9fa;
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  
  button {
    padding: var(--spacing-3) var(--spacing-5);
    border: none;
    border-radius: var(--border-radius);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.primary {
      background-color: var(--color-secondary);
      color: white;
      
      &:hover {
        background-color: #1f1e3a;
      }
      
      &:disabled {
        background-color: var(--color-text-light);
        cursor: not-allowed;
      }
    }
    
    &.secondary {
      background-color: var(--color-border);
      color: var(--color-text);
      
      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 6px;
  background: var(--color-border);
  border-radius: 3px;
  overflow: hidden;
  margin: var(--spacing-3) 0;
  
  .progress-fill {
    height: 100%;
    background: var(--color-secondary);
    width: ${props => props.progress}%;
    transition: width 0.3s ease;
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: var(--spacing-3);
  border-radius: var(--border-radius);
  margin: var(--spacing-3) 0;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return 'background: #dcfce7; color: #166534; border: 1px solid #bbf7d0;';
      case 'error':
        return 'background: #fee2e2; color: #991b1b; border: 1px solid #fecaca;';
      case 'info':
        return 'background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe;';
      default:
        return 'background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;';
    }
  }}
`;

interface Customer {
  email: string;
  first_name: string;
  last_name: string;
}

interface EmailComposerProps {
  customers: Customer[];
  onClose: () => void;
}

const emailTemplates = {
  newsletter: {
    subject: "Nytt från Lillaflora - Se våra senaste produkter",
    content: `<p>Hej {{customer_name}}!</p>

<p>Vi hoppas att du har det bra! Vi ville bara dela med oss av några spännande nyheter från Lillaflora.</p>

<h3>🌸 Nya produkter som du kommer att älska</h3>
<p>Vi har nyligen utökat vårt sortiment med ännu fler vackra inredningsdetaljer som skapar mys och stämning i ditt hem.</p>

<h3>🎁 Särskilt erbjudande för dig</h3>
<p>Som en av våra värdefulla kunder vill vi ge dig <strong>15% rabatt</strong> på din nästa beställning. Använd koden <strong>KUND15</strong> vid kassan.</p>

<p>Besök vår webbshop på <a href="https://lillaflora.nu">lillaflora.nu</a> för att se alla våra produkter.</p>

<p>Tack för att du handlar hos oss!</p>`
  },
  promotional: {
    subject: "🎉 Exklusiv rea - Upp till 30% rabatt",
    content: `<p>Hej {{customer_name}}!</p>

<p>Vi har stora nyheter att dela med dig! Vår <strong>exklusiva rea</strong> är här med fantastiska rabatter på många av våra populäraste produkter.</p>

<h3>✨ Vad du kan förvänta dig:</h3>
<ul>
<li>Upp till 30% rabatt på utvalde produkter</li>
<li>Fri frakt på order över 500 kr</li>
<li>Begränsad tid - erbjudandet gäller till måndag</li>
</ul>

<p>Som en av våra trogna kunder får du <strong>tidigt tillträde</strong> till rean innan den öppnas för allmänheten.</p>

<p><strong>👉 <a href="https://lillaflora.nu">Shoppa nu på lillaflora.nu</a></strong></p>

<p>Missa inte denna chans att uppdatera din inredning till fantastiska priser!</p>`
  },
  thankyou: {
    subject: "Tack för ditt förtroende - Lillaflora",
    content: `<p>Kära {{customer_name}},</p>

<p>Vi vill ta tillfället i akt att tacka dig för ditt fortsatta förtroende och stöd till Lillaflora.</p>

<p>Dina köp gör det möjligt för oss att fortsätta erbjuda högkvalitativa svenska inredningsdetaljer som skapar mys och stämning.</p>

<h3>🙏 Varför ditt stöd betyder så mycket</h3>
<p>Som ett litet företag är varje kund viktig för oss. Din feedback och dina köp hjälper oss att växa och förbättra vårt sortiment.</p>

<p>Om du har några frågor, förslag eller bara vill säga hej, tveka inte att höra av dig. Vi älskar att höra från våra kunder!</p>

<p>Ha en underbar dag!</p>`
  }
};

const EmailComposer: React.FC<EmailComposerProps> = ({ customers, onClose }) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [senderName, setSenderName] = useState('Lillaflora Team');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const handleTemplateSelect = (templateKey: keyof typeof emailTemplates) => {
    const template = emailTemplates[templateKey];
    setSubject(template.subject);
    setContent(template.content);
  };

  const handleSendEmails = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject || !content) {
      setStatus({ type: 'error', message: 'Ämne och innehåll måste fyllas i.' });
      return;
    }
    
    setSending(true);
    setProgress(0);
    setStatus({ type: 'info', message: 'Skickar email...' });
    
    try {
      const emailData = {
        emails: customers,
        subject,
        content,
        sender_name: senderName
      };
      
      const result = await apiService.sendBulkEmail(emailData);
      
      setProgress(100);
      setStatus({ 
        type: 'success', 
        message: `Email skickades framgångsrikt! ${result.successful_sends} av ${result.total_attempts} emails skickades.` 
      });
      
      // Close modal after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (err: any) {
      setStatus({ type: 'error', message: `Fel vid skickande av email: ${err.message}` });
    } finally {
      setSending(false);
    }
  };

  const previewContent = content
    .replace(/{{customer_name}}/g, customers[0]?.first_name + ' ' + customers[0]?.last_name || 'Kära kund')
    .replace(/{{first_name}}/g, customers[0]?.first_name || 'Kund')
    .replace(/{{email}}/g, customers[0]?.email || 'exempel@email.se');

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>✉️ Skicka email till {customers.length} kunder</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </ModalHeader>
        
        <ModalBody>
          <EmailForm onSubmit={handleSendEmails}>
            <FormSection>
              <h4>📧 Mottagare</h4>
              <RecipientsList>
                {customers.slice(0, 10).map((customer) => (
                  <div key={customer.email} className="recipient-item">
                    <div>
                      <span className="email">{customer.email}</span>
                      <span className="name"> - {customer.first_name} {customer.last_name}</span>
                    </div>
                  </div>
                ))}
                {customers.length > 10 && (
                  <div className="recipient-item">
                    <div style={{ fontStyle: 'italic', color: 'var(--color-text-light)' }}>
                      ... och {customers.length - 10} fler kunder
                    </div>
                  </div>
                )}
              </RecipientsList>
            </FormSection>
            
            <FormSection>
              <h4>📝 Email-innehåll</h4>
              
              <div>
                <label>Välj mall (valfritt)</label>
                <TemplateButtons>
                  <TemplateButton type="button" onClick={() => handleTemplateSelect('newsletter')}>
                    📰 Nyhetsbrev
                  </TemplateButton>
                  <TemplateButton type="button" onClick={() => handleTemplateSelect('promotional')}>
                    🎉 Kampanj
                  </TemplateButton>
                  <TemplateButton type="button" onClick={() => handleTemplateSelect('thankyou')}>
                    🙏 Tack-email
                  </TemplateButton>
                </TemplateButtons>
              </div>
              
              <FormGroup>
                <label>Avsändarnamn</label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Lillaflora Team"
                />
              </FormGroup>
              
              <FormGroup>
                <label>Ämnesrad</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Skriv din ämnesrad här..."
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <label>Email-innehåll (HTML stöds)</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Skriv ditt meddelande här..."
                  required
                />
                <div className="help-text">
                  Du kan använda {`{{customer_name}}`}, {`{{first_name}}`}, eller {`{{email}}`} för personalisering.
                </div>
              </FormGroup>
            </FormSection>
            
            {content && (
              <PreviewSection>
                <h5>👀 Förhandsvisning</h5>
                <div className="preview-content">
                  <div dangerouslySetInnerHTML={{ __html: previewContent }} />
                </div>
              </PreviewSection>
            )}
            
            {status && <StatusMessage type={status.type}>{status.message}</StatusMessage>}
            
            {sending && (
              <ProgressBar progress={progress}>
                <div className="progress-fill" />
              </ProgressBar>
            )}
          </EmailForm>
        </ModalBody>
        
        <ModalFooter>
          <button type="button" className="secondary" onClick={onClose} disabled={sending}>
            Avbryt
          </button>
          <button 
            type="submit" 
            className="primary" 
            onClick={handleSendEmails}
            disabled={sending || !subject || !content}
          >
            {sending ? '📤 Skickar...' : `📤 Skicka till ${customers.length} kunder`}
          </button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EmailComposer;